import { useMemo } from 'react'
import { ProjectFormModel } from '../model'
import {
  BooleanModifier,
  DateTermModifier,
  FormModelModifiers,
  ReferencedEntityModifier,
  SelectOptionModifier,
  TextModifier,
} from '../../../containers/SingleSheetV2/model/formModelModifiers'
import { ValidateSingleSheetPropertyValue } from '../../../containers/SingleSheetV2/hooks/formValidation'
import { DateTermV2 } from '../../../../domain/value-object/DateTermV2'
import {
  DEFAULT_PROJECT_STATUS,
  DEFAULT_WBS_ITEM_CODE_FORMAT,
  ProjectStatus,
  WbsItemCodeFormat,
} from '../../../../domain/entity/ProjectEntity'

export type OnChangeProject = <K extends keyof ProjectFormModel>(
  path: K,
  value: ProjectFormModel[K]
) => void

// 基本情報テーブルの各項目用の modifier を構築する。各 modifier は
// フォームモデルを更新し、その項目のバリデーションを再実行する。
export const useProjectModelModifier = (
  onChangeProject: OnChangeProject,
  validate: ValidateSingleSheetPropertyValue
): FormModelModifiers => {
  return useMemo<FormModelModifiers>(() => {
    const text =
      (
        field: 'code' | 'iconUrl' | 'displayName' | 'officialName' | 'mainEmail'
      ): TextModifier =>
      v => {
        onChangeProject(field, v ?? '')
        validate(field, v)
      }
    const entity =
      (
        field: 'businessClient' | 'division' | 'projectManager'
      ): ReferencedEntityModifier =>
      v => {
        onChangeProject(field, v)
        validate(field, v)
      }
    const onChangeStatus: SelectOptionModifier = v => {
      onChangeProject('status', (v ?? DEFAULT_PROJECT_STATUS) as ProjectStatus)
      validate('status', v)
    }
    const onChangeWbsItemCodeFormat: SelectOptionModifier = v => {
      onChangeProject(
        'wbsItemCodeFormat',
        (v ?? DEFAULT_WBS_ITEM_CODE_FORMAT) as WbsItemCodeFormat
      )
      validate('wbsItemCodeFormat', v)
    }
    const onChangeScheduledDate: DateTermModifier = v => {
      const value: DateTermV2 = v ?? {
        startDate: undefined,
        endDate: undefined,
      }
      onChangeProject('scheduledDate', value)
      validate('scheduledDate', value)
    }
    const onChangeGithubReviewerSyncEnabled: BooleanModifier = v => {
      onChangeProject('githubReviewerSyncEnabled', v ?? false)
      validate('githubReviewerSyncEnabled', v)
    }

    return {
      code: text('code'),
      iconUrl: text('iconUrl'),
      displayName: text('displayName'),
      officialName: text('officialName'),
      mainEmail: text('mainEmail'),
      businessClient: entity('businessClient'),
      division: entity('division'),
      projectManager: entity('projectManager'),
      status: onChangeStatus,
      wbsItemCodeFormat: onChangeWbsItemCodeFormat,
      scheduledDate: onChangeScheduledDate,
      githubReviewerSyncEnabled: onChangeGithubReviewerSyncEnabled,
    }
  }, [onChangeProject, validate])
}
